/**
 * Performance Monitor
 * Tracks and reports performance metrics for map rendering
 */

export interface PerformanceMetrics {
  csvFileRead: number
  csvWorkerParsing: number
  boundsCalculation: number
  elementCreation: number
  elementSorting: number
  graphAddition: number
  totalTime: number
}

class PerformanceMonitor {
  private metrics: Partial<PerformanceMetrics> = {}
  private startTime: number | null = null
  private measurements: Map<string, number> = new Map()

  /**
   * Start tracking overall performance
   */
  startSession() {
    this.metrics = {}
    this.startTime = performance.now()
    console.log('%c🚀 Performance Monitoring Started', 'background: #4CAF50; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold')
  }

  /**
   * Record a metric
   */
  record(key: keyof PerformanceMetrics, value: number) {
    this.metrics[key] = value
  }

  /**
   * Start a measurement
   */
  start(label: string): void {
    this.measurements.set(label, performance.now())
  }

  /**
   * End a measurement and record the duration
   */
  end(label: string): void {
    const startTime = this.measurements.get(label)
    if (startTime) {
      const duration = performance.now() - startTime
      // Store in metrics if it's a known key, otherwise keep in measurements
      if (label in this.metrics) {
        this.record(label as keyof PerformanceMetrics, duration)
      }
      this.measurements.set(`${label}_duration`, duration)
    }
  }

  /**
   * Get a recorded measurement
   */
  getMetric(label: string): number | undefined {
    return (this.metrics as any)[label] || this.measurements.get(`${label}_duration`)
  }

  /**
   * End session and generate report
   */
  endSession() {
    if (this.startTime === null) {
      console.warn('Performance monitoring was not started')
      return
    }

    const totalTime = performance.now() - this.startTime
    this.metrics.totalTime = totalTime

    this.generateReport()
    this.startTime = null
  }

  /**
   * Generate visual performance report
   */
  private generateReport() {
    const m = this.metrics as PerformanceMetrics

    console.log('%c📊 Performance Report', 'background: #2196F3; color: white; padding: 4px 8px; border-radius: 4px; font-weight: bold; font-size: 14px')
    console.log('')

    // Create table data
    const stages = [
      { name: '📁 File Read', time: m.csvFileRead || 0 },
      { name: '⚙️ CSV Parsing (Worker)', time: m.csvWorkerParsing || 0 },
      { name: '📐 Bounds Calculation', time: m.boundsCalculation || 0 },
      { name: '🎨 Element Creation', time: m.elementCreation || 0 },
      { name: '🔢 Element Sorting', time: m.elementSorting || 0 },
      { name: '➕ Graph Addition (Frozen)', time: m.graphAddition || 0 },
    ]

    // Calculate percentages
    const total = m.totalTime || 1
    const stagesWithPercent = stages.map(s => ({
      ...s,
      percent: (s.time / total * 100).toFixed(1)
    }))

    // Print table
    console.table(stagesWithPercent.map(s => ({
      'Stage': s.name,
      'Time (ms)': s.time.toFixed(2),
      'Percentage': `${s.percent}%`,
      'Bar': '█'.repeat(Math.round(s.time / total * 50))
    })))

    console.log('')
    console.log(`%c⏱️ Total Time: ${total.toFixed(2)}ms`, 'font-weight: bold; font-size: 14px')

    // Identify bottleneck
    const slowest = stagesWithPercent.reduce((max, s) => s.time > max.time ? s : max, stagesWithPercent[0])
    console.log(`%c🐌 Slowest Stage: ${slowest.name} (${slowest.time.toFixed(2)}ms, ${slowest.percent}%)`,
      'color: #FF9800; font-weight: bold')

    // Performance rating
    const rating = this.getPerformanceRating(total)
    console.log(`%c${rating.icon} Performance Rating: ${rating.label}`, `color: ${rating.color}; font-weight: bold; font-size: 14px`)
    console.log('')
  }

  /**
   * Get performance rating based on total time
   */
  private getPerformanceRating(totalTime: number) {
    if (totalTime < 1000) {
      return { icon: '⚡', label: 'Excellent (<1s)', color: '#4CAF50' }
    } else if (totalTime < 2000) {
      return { icon: '✅', label: 'Good (1-2s)', color: '#8BC34A' }
    } else if (totalTime < 3000) {
      return { icon: '⚠️', label: 'Fair (2-3s)', color: '#FFC107' }
    } else if (totalTime < 5000) {
      return { icon: '🐌', label: 'Slow (3-5s)', color: '#FF9800' }
    } else {
      return { icon: '❌', label: 'Very Slow (>5s)', color: '#F44336' }
    }
  }
}

// Export singleton
export const perfMonitor = new PerformanceMonitor()
